// src/pages/SupportPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMail, FiPhone } from 'react-icons/fi';
import { MdDashboard } from 'react-icons/md';

import { useAuth } from '../context/AuthContext';
import { useRemoteCss } from '../hooks/useRemoteCss';
import axiosInstance from '../api/axiosInstance';
import CookieBanner from '../components/CookieBanner';

import './cx.css';

/* ───────── tiny loader ───────── */
const Loader = () => (
  <>
    <div className="loader-wrapper">
      <div className="loader" />
    </div>
    <style>{`
      .loader-wrapper{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#f7f7f7;z-index:9999}
      .loader{width:60px;aspect-ratio:1;display:grid}
      .loader:before,.loader:after{content:"";grid-area:1/1;--c:no-repeat radial-gradient(farthest-side,var(--accent, #b89b6e) 92%,#0000);
        background:var(--c)50%0,var(--c)50%100%,var(--c)100%50%,var(--c)0 50%;background-size:14px 14px;animation:l12 1s infinite}
      .loader:before{margin:5px;filter:hue-rotate(15deg);background-size:9px 9px;animation-timing-function:linear}
      @keyframes l12{100%{transform:rotate(.5turn)}}
    `}</style>
  </>
);

/* Floating Theme Dock */
function ThemeDock({ theme, setTheme }) {
  const DOCK_KEY = 'pref-theme-dock';
  const [side, setSide] = useState(() => {
    try {
      return localStorage.getItem(DOCK_KEY) === 'right' ? 'right' : 'left';
    } catch {
      return 'left';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DOCK_KEY, side);
    } catch {}
  }, [side]);

  return (
    <div className={`theme-dock ${side}`} role="group" aria-label="Theme picker" style={{ zIndex: 1200 }}>
      <button
        className={`dock-dot dot-brown ${theme === 'brown' ? 'active' : ''}`}
        onClick={() => setTheme('brown')}
        title="Use Brown (original) theme"
        aria-label="Use Brown (original) theme"
      />
      <button
        className={`dock-dot dot-green ${theme === 'green' ? 'active' : ''}`}
        onClick={() => setTheme('green')}
        title="Use Green theme"
        aria-label="Use Green theme"
      />
      <button
        className="dock-side-toggle"
        onClick={() => setSide((s) => (s === 'left' ? 'right' : 'left'))}
        title={`Move dock to ${side === 'left' ? 'right' : 'left'} side`}
        aria-label={`Move dock to ${side === 'left' ? 'right' : 'left'} side`}
      >
        ↔
      </button>
    </div>
  );
}

/**
 * Public Support page (read-only).
 * Shape:
 *  - New: { countryContacts: [{ country, supportNumber, supportEmail, contactPerson, supportHours, note }] }
 *  - Legacy: { supportNumber/supportEmail/... + countriesSupported[] }
 */
const emptyContact = () => ({
  country: '',
  supportNumber: '',
  supportEmail: '',
  contactPerson: '',
  supportHours: '',
  note: '',
});

function normalizeContacts(list) {
  const arr = Array.isArray(list) ? list : [];
  const seen = new Set();
  const out = [];

  for (const raw of arr) {
    const item = { ...emptyContact(), ...(raw || {}) };
    item.country = String(item.country || '').trim();
    if (!item.country) continue;

    const key = item.country.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    item.supportNumber = String(item.supportNumber || '').trim();
    item.supportEmail = String(item.supportEmail || '').trim();
    item.contactPerson = String(item.contactPerson || '').trim();
    item.supportHours = String(item.supportHours || '').trim();
    item.note = String(item.note || '').trim();

    out.push(item);
  }

  out.sort((a, b) => a.country.localeCompare(b.country));
  return out;
}

function synthesizeContactsFromLegacy(incoming) {
  const legacyCountries = Array.isArray(incoming?.countriesSupported) ? incoming.countriesSupported : [];
  const countries = legacyCountries.map((c) => String(c || '').trim()).filter(Boolean);

  const base = {
    supportNumber: String(incoming?.supportNumber || '').trim(),
    supportEmail: String(incoming?.supportEmail || '').trim(),
    contactPerson: String(incoming?.contactPerson || '').trim(),
    supportHours: String(incoming?.supportHours || '').trim(),
    note: String(incoming?.note || '').trim(),
  };

  if (!countries.length) return [];
  return normalizeContacts(countries.map((country) => ({ country, ...base })));
}

export default function SupportPage() {
  const { authData } = useAuth();
  const token = authData?.token;

  const navigate = useNavigate();

  // Theme persistence
  const THEME_KEY = 'pref-theme';
  const initialTheme = (() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === 'brown' || saved === 'green') return saved;
    } catch {}
    return 'brown';
  })();

  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {}
  }, [theme]);

  // Optional remote CSS
  const cssUrl = `${axiosInstance.defaults.baseURL}/styles/SupportPage`;
  const cssReady = useRemoteCss(cssUrl);

  // Minimum delay so loader doesn't flicker
  const [minDelayDone, setMinDelayDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinDelayDone(true), 300);
    return () => clearTimeout(t);
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    document.title = 'Nnetego | Support';

    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'Support contacts by country for Nnetego Hotel Management System.';
    document.head.appendChild(metaDescription);

    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  const fetchSupport = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axiosInstance.get('/pages/support');
      const incoming = res?.data || {};

      const incomingContacts = normalizeContacts(incoming?.countryContacts);
      const finalContacts = incomingContacts.length ? incomingContacts : synthesizeContactsFromLegacy(incoming);

      setContacts(finalContacts);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data ||
        e?.message ||
        'Failed to load support details.';
      setError(typeof msg === 'string' ? msg : 'Failed to load support details.');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countries = useMemo(() => contacts.map((c) => c.country), [contacts]);

  if (!cssReady || !minDelayDone) return <Loader />;

  return (
    <div className="lux-layout">
      <ThemeDock theme={theme} setTheme={setTheme} />

      <div className="lux-topbar">Support Contacts by Country</div>

      <header className="lux-header support-header">
        <div className="lux-container header-inner">
          <div className="lux-logo" onClick={() => navigate('/')} role="button" tabIndex={0}>
            <img src="/images/logo.png" alt="Nnetego" style={{ height: 28 }} />
          </div>

          <div className="header-right">
            <button className="lux-btn-ghost" onClick={() => navigate(-1)}>
              <FiArrowLeft /> Back
            </button>

            {token ? (
              <button className="lux-cta" onClick={() => navigate('/dashboard')}>
                <MdDashboard style={{ marginRight: 8 }} /> Dashboard
              </button>
            ) : (
              <button className="lux-cta" onClick={() => navigate('/login')}>
                Get Started
              </button>
            )}
          </div>
        </div>
      </header>

      <section className="lux-section lux-section--ivory support-wrap">
        <div className="lux-container">
          <div className="support-head">
            <div>
              <h1 className="lux-h2 support-title">Support</h1>
              <p className="lux-copy support-sub">
                Country-specific support contacts. Select the relevant country below to find the correct phone, email, contact person, hours, and notes.
              </p>
            </div>

            <div className="support-actions">
              <button className="lux-btn-ghost" onClick={fetchSupport} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>

          {error && <div className="support-alert">{error}</div>}

          <div className="support-grid">
            <div className="support-card">
              <div className="support-card-head">
                <div className="support-card-title">Country Support Contacts</div>
                <div className="support-badge">Per-country details</div>
              </div>

              <div className="country-sections">
                {contacts.length ? (
                  contacts.map((c) => (
                    <div key={c.country} className="country-section">
                      <div className="country-title-row">
                        <div className="country-title">{c.country}:</div>
                      </div>
                      <div className="country-sep" aria-hidden="true" />

                      <div className="country-lines">
                        <div className="country-line">
                          <span className="country-k">
                            <FiPhone /> Phone
                          </span>
                          <span className="country-v">
                            {c.supportNumber ? (
                              <a className="support-item-value" href={`tel:${c.supportNumber}`}>
                                {c.supportNumber}
                              </a>
                            ) : (
                              <span className="support-item-muted">Not set</span>
                            )}
                          </span>
                        </div>

                        <div className="country-line">
                          <span className="country-k">
                            <FiMail /> Email
                          </span>
                          <span className="country-v">
                            {c.supportEmail ? (
                              <a className="support-item-value" href={`mailto:${c.supportEmail}`}>
                                {c.supportEmail}
                              </a>
                            ) : (
                              <span className="support-item-muted">Not set</span>
                            )}
                          </span>
                        </div>

                        <div className="country-line">
                          <span className="country-k">@ Contact</span>
                          <span className="country-v">{c.contactPerson || <span className="support-item-muted">Not set</span>}</span>
                        </div>

                        <div className="country-line">
                          <span className="country-k">⏱ Hours</span>
                          <span className="country-v">{c.supportHours || <span className="support-item-muted">Not set</span>}</span>
                        </div>

                        <div className="country-note">
                          <div className="country-note-label">Notes</div>
                          {c.note ? (
                            <div className="country-note-body">{c.note}</div>
                          ) : (
                            <div className="support-item-muted">No notes.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="support-item-muted">No country contacts configured yet.</div>
                )}
              </div>
            </div>

            <div className="support-card">
              <div className="support-card-head">
                <div className="support-card-title">Countries</div>
                <div className="support-badge">Configured</div>
              </div>

              <div className="country-chips">
                {countries.length ? (
                  countries.map((name) => (
                    <span key={name} className="chip view">
                      {name}
                    </span>
                  ))
                ) : (
                  <div className="support-item-muted">None yet.</div>
                )}
              </div>

              <div className="support-divider" />

              {/* UPDATED COPY (per your request) */}
              <div className="support-item-muted">
                Support is available only for configured countries/regions. Please contact your administrator to add additional regions and support contacts.
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="lux-footer">
        <div className="lux-container">
          <div className="lux-legal">
            <div className="lux-legal-copy">
              © {new Date().getFullYear()} Nnetego. All rights reserved. <a href="/privacy">Privacy</a> •{' '}
              <a href="/terms">Terms</a>
            </div>
            <div className="lux-legal-credits">
              Designed by{' '}
              <a href="https://lanesands.com" target="_blank" rel="noopener noreferrer">
                Lanesands
              </a>
              .
            </div>
          </div>
        </div>
      </footer>

      <CookieBanner />

      <style>{`
        /* sticky footer base */
        html, body, #root { height: 100%; }
        body { margin: 0; }
        .lux-layout{ min-height:100vh; display:flex; flex-direction:column; }
        .support-wrap{ flex:1 0 auto; }
        .lux-footer{ margin-top:auto; flex-shrink:0; }

        .lux-topbar{
          position:sticky; top:0; z-index:1300;
          height:32px; display:flex; align-items:center; justify-content:center;
          padding:0 24px; font-size:.76rem; letter-spacing:.18em; text-transform:uppercase;
          color:#f6f2ea; background:rgba(10,8,6,.95);
        }
        .lux-header{
          position:sticky; top:32px; z-index:1290;
          backdrop-filter:blur(22px);
          border-bottom:1px solid rgba(255,255,255,.08);
        }
        .support-header{ box-shadow:0 16px 45px rgba(0,0,0,.12); }
        .header-inner{ display:flex; align-items:center; justify-content:space-between; padding:12px 40px; }
        @media (max-width:768px){ .header-inner{padding:10px 18px;} }

        .theme-dock{
          position:fixed; top:50%; transform:translateY(-50%);
          left:24px; padding:6px; border-radius:999px;
          background:rgba(15,11,8,.75);
          box-shadow:0 18px 40px rgba(0,0,0,.4);
          backdrop-filter:blur(16px);
          display:flex; flex-direction:column; gap:6px;
        }
        .theme-dock.right{ left:auto; right:24px; }
        .dock-dot{ width:18px; height:18px; border-radius:999px; border:1px solid rgba(255,255,255,.6); cursor:pointer; opacity:.8; }
        .dot-brown{ background:#b89b6e; }
        .dot-green{ background:#4f7d5b; }
        .dock-dot.active{ opacity:1; box-shadow:0 0 0 2px rgba(255,255,255,.85); transform:scale(1.05); }
        .dock-side-toggle{
          width:20px;height:20px;border-radius:999px;border:1px solid rgba(255,255,255,.4);
          background:transparent;color:#f8f4ec;font-size:.7rem;cursor:pointer;display:flex;align-items:center;justify-content:center;
        }
        @media (max-width:768px){ .theme-dock{ top:auto; bottom:80px; transform:none; } }

        .lux-cta{
          border:none; border-radius:999px; padding:10px 26px;
          font-size:.86rem; font-weight:600; letter-spacing:.16em; text-transform:uppercase;
          background:linear-gradient(135deg,#b89b6e,#e6cf9d);
          color:#25170a; cursor:pointer;
          display:inline-flex; align-items:center; gap:8px;
          box-shadow:0 16px 38px rgba(0,0,0,.32);
        }
        .lux-btn-ghost{
          border-radius:999px; padding:9px 22px;
          border:1px solid rgba(255,255,255,.76);
          background:rgba(15,11,8,.18);
          color:#f8f4ec; font-size:.84rem; letter-spacing:.12em; text-transform:uppercase;
          cursor:pointer; display:inline-flex; align-items:center; gap:8px;
          backdrop-filter:blur(10px);
        }

        .header-right{
  display:flex;
  align-items:center;
  gap:12px;
}

/* Prevent "Get Started" from wrapping to two lines */
.lux-cta{
  white-space: nowrap;
  flex-shrink: 0;
  width: max-content; /* keeps the pill sized to its content */
}

        .support-wrap{ padding-top:36px; }
        .support-head{
          display:flex; align-items:flex-start; justify-content:space-between; gap:18px;
          margin-bottom:18px;
        }
        @media (max-width:900px){ .support-head{ flex-direction:column; } }
        .support-title{ margin:0; }
        .support-sub{ margin-top:8px; max-width:760px; }

        .support-actions{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; }

        .support-alert{
          margin:12px 0 18px;
          padding:12px 14px;
          border-radius:14px;
          background:rgba(190, 40, 40, .08);
          border:1px solid rgba(190, 40, 40, .22);
          color:#7b1a1a;
        }

        .support-grid{
          display:grid;
          grid-template-columns: 1.15fr .85fr;
          gap:16px;
          align-items:start;
        }
        @media (max-width: 980px){ .support-grid{ grid-template-columns: 1fr; } }

        .support-card{
          background:var(--card-bg,#fff);
          border:1px solid var(--card-border,#eee);
          border-radius:18px;
          padding:16px 16px 14px;
          box-shadow:0 1px 2px rgba(0,0,0,.03);
        }
        .support-card-head{
          display:flex; align-items:center; justify-content:space-between;
          gap:10px; margin-bottom:12px;
        }
        .support-card-title{
          font-weight:700; letter-spacing:.08em; text-transform:uppercase;
          font-size:.86rem; color:var(--heading-color,#111);
        }
        .support-badge{
          font-size:.74rem;
          letter-spacing:.12em;
          text-transform:uppercase;
          color:var(--accent,#b89b6e);
          background:rgba(184,155,110,.12);
          border:1px solid rgba(184,155,110,.35);
          padding:6px 10px;
          border-radius:999px;
          white-space:nowrap;
        }

        .support-item-muted{ color:var(--copy-color,#6b6b6b); }
        .support-item-value{ color:var(--heading-color,#15110d); font-weight:600; text-decoration:none; }
        .support-item-value:hover{ text-decoration:underline; }

        .country-sections{ display:flex; flex-direction:column; gap:18px; }
        .country-section{
          border:1px solid rgba(0,0,0,.06);
          background:rgba(0,0,0,.015);
          border-radius:16px;
          padding:14px;
        }
        .country-title{ font-weight:800; font-size:1.05rem; color:var(--heading-color,#15110d); }
        .country-sep{
          margin:10px 0 12px;
          height:1px;
          background:repeating-linear-gradient(to right, rgba(0,0,0,.16), rgba(0,0,0,.16) 10px, rgba(0,0,0,0) 10px, rgba(0,0,0,0) 18px);
          border-radius:999px;
        }
        .country-lines{ display:flex; flex-direction:column; gap:10px; }
        .country-line{
          display:grid;
          grid-template-columns: 140px 1fr;
          gap:12px;
          align-items:start;
        }
        @media (max-width:520px){ .country-line{ grid-template-columns: 1fr; gap:6px; } }
        .country-k{
          font-size:.82rem; letter-spacing:.12em; text-transform:uppercase;
          color:#8b7f6c; display:inline-flex; align-items:center; gap:8px;
        }
        .country-v{ color:var(--copy-color,#534c42); }

        .country-note-label{
          font-size:.76rem; letter-spacing:.14em; text-transform:uppercase; color:#8b7f6c;
          margin-bottom:6px;
        }
        .country-note-body{
          color:var(--copy-color,#534c42);
          line-height:1.55;
          background:rgba(255,255,255,.7);
          border:1px solid rgba(0,0,0,.06);
          border-radius:14px;
          padding:12px;
          white-space:pre-wrap;
        }

        .support-divider{ height:1px; background:rgba(0,0,0,.06); margin:16px 0; }

        .country-chips{ display:flex; flex-wrap:wrap; gap:8px; margin-top:10px; }
        .chip{
          display:inline-flex; align-items:center; gap:8px;
          padding:8px 12px;
          border-radius:999px;
          border:1px solid rgba(0,0,0,.08);
          background:rgba(0,0,0,.02);
          color:#2a241c;
          font-weight:600;
        }
        .chip.view{
          border-color:rgba(184,155,110,.35);
          background:rgba(184,155,110,.10);
        }
      `}</style>
    </div>
  );
}
